import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";

import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  TrainOutlined,
  SecurityOutlined,
  LoginRounded,
  YardOutlined,
  BuildOutlined,
  FactCheckOutlined,
  ExitToAppOutlined,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const { login, loading } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!username.trim() || !password) {
      setError("Please enter username and password");
      return;
    }

    const result = await login(
      username.trim(),
      password,
      rememberMe
    );

    if (!result.success) {
      setError(result.message);
      return;
    }

    navigate("/dashboard", {
      replace: true,
    });
  };

  const features = [
    {
      icon: <YardOutlined />,
      title: "Yard Entry",
      description: "Receive coach into workshop",
    },
    {
      icon: <BuildOutlined />,
      title: "Shop Work",
      description: "Assign and track coach work",
    },
    {
      icon: <FactCheckOutlined />,
      title: "Inspection",
      description: "Quality check and approval",
    },
    {
      icon: <ExitToAppOutlined />,
      title: "Outturn",
      description: "Complete and release coach",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #eaf2ff 0%, #f5f8ff 45%, #dbeafe 100%)",
        p: {
          xs: 0,
          sm: 2,
          md: 3,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1250,

          minHeight: {
            xs: "100vh",
            sm: 680,
          },

          display: "flex",
          overflow: "hidden",

          borderRadius: {
            xs: 0,
            sm: 5,
          },

          border: "1px solid",
          borderColor: "rgba(148,163,184,0.2)",

          boxShadow:
            "0 30px 80px rgba(30,64,175,0.18)",

          backgroundColor: "#fff",
        }}
      >
        {/* =====================================================
            LEFT LOGIN PANEL
        ====================================================== */}

        <Box
          sx={{
            width: {
              xs: "100%",
              md: "47%",
            },

            display: "flex",
            flexDirection: "column",
            justifyContent: "center",

            p: {
              xs: 3,
              sm: 5,
              md: 6,
              lg: 7,
            },

            position: "relative",

            background:
              "linear-gradient(180deg, #ffffff 0%, #fafdff 100%)",
          }}
        >
          {/* BRAND */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mb: {
                xs: 4,
                md: 6,
              },
            }}
          >
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: 2.5,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                color: "#fff",

                background:
                  "linear-gradient(135deg, #2563eb, #1d4ed8)",

                boxShadow:
                  "0 10px 25px rgba(37,99,235,0.28)",
              }}
            >
              <TrainOutlined
                sx={{
                  fontSize: 32,
                }}
              />
            </Box>

            <Box>
              <Typography
                fontWeight={800}
                sx={{
                  fontSize: {
                    xs: "0.95rem",
                    sm: "1.05rem",
                  },
                  color: "#0f172a",
                }}
              >
                Coach Information System
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  letterSpacing: 1.2,
                  fontWeight: 600,
                }}
              >
                WORKSHOP MANAGEMENT
              </Typography>
            </Box>
          </Stack>

          {/* HEADING */}

          <Box sx={{ mb: 3.5 }}>
            <Typography
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: "2.4rem",
                  md: "2.7rem",
                },

                lineHeight: 1.1,

                fontWeight: 800,

                color: "#102a56",
              }}
            >
              Welcome{" "}
              <Box
                component="span"
                sx={{
                  color: "#1976d2",
                }}
              >
                Back
              </Box>
            </Typography>

            <Typography
              sx={{
                mt: 1.2,
                color: "#64748b",
                fontSize: {
                  xs: "0.9rem",
                  sm: "0.98rem",
                },
              }}
            >
              Sign in to continue to your
              workshop dashboard.
            </Typography>
          </Box>

          {/* ERROR */}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* FORM */}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              maxWidth: 500,
            }}
          >
            {/* USERNAME */}

            <TextField
              fullWidth
              label="Username or Email"
              value={username}
              onChange={(e) =>
                setUsername(e.target.value)
              }
              autoFocus
              autoComplete="username"
              sx={{
                mb: 2,

                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",

                  "&:hover fieldset": {
                    borderColor: "#2563eb",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#2563eb",
                    borderWidth: 1.5,
                  },
                },
              }}
            />

            {/* PASSWORD */}

            <TextField
              fullWidth
              label="Password"
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              autoComplete="current-password"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",

                  "&:hover fieldset": {
                    borderColor: "#2563eb",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#2563eb",
                    borderWidth: 1.5,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{
                        color: "#94a3b8",
                        fontSize: 21,
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPassword(
                          (prev) => !prev
                        )
                      }
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* REMEMBER */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) =>
                      setRememberMe(
                        e.target.checked
                      )
                    }
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Remember me
                  </Typography>
                }
              />
            </Box>

            {/* SIGN IN */}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              endIcon={
                !loading && (
                  <LoginRounded />
                )
              }
              sx={{
                mt: 2,

                height: 54,

                borderRadius: 2,

                textTransform: "none",

                fontSize: "1rem",

                fontWeight: 700,

                background:
                  "linear-gradient(135deg, #1683f5, #1464d8)",

                boxShadow:
                  "0 10px 25px rgba(37,99,235,0.25)",

                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0f73dc, #1255bd)",

                  boxShadow:
                    "0 14px 30px rgba(37,99,235,0.32)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  color="inherit"
                />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>

          {/* SECURITY */}

          <Box
            sx={{
              width: "100%",
              maxWidth: 500,
              mt: 4,
            }}
          >
            <Divider sx={{ mb: 2.5 }} />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <SecurityOutlined
                sx={{
                  fontSize: 19,
                  color: "#64748b",
                }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Secure access for authorized
                workshop users only.
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 2,
              }}
            >
              © {new Date().getFullYear()} Coach
              Information System. All rights reserved.
            </Typography>
          </Box>
        </Box>

        {/* =====================================================
            RIGHT WORKSHOP PANEL
        ====================================================== */}

        <Box
          sx={{
            display: {
              xs: "none",
              md: "flex",
            },

            width: "53%",

            position: "relative",

            overflow: "hidden",

            background:
              "linear-gradient(135deg, #082f75 0%, #0753b7 45%, #0b67d1 100%)",

            color: "#fff",

            flexDirection: "column",

            justifyContent: "center",

            px: {
              md: 5,
              lg: 7,
            },

            py: 5,
          }}
        >
          {/* Decorative circle */}

          <Box
            sx={{
              position: "absolute",

              width: 520,
              height: 520,

              borderRadius: "50%",

              right: -250,
              top: -150,

              background:
                "rgba(96,165,250,0.18)",
            }}
          />

          <Box
            sx={{
              position: "absolute",

              width: 400,
              height: 400,

              borderRadius: "50%",

              left: -260,
              bottom: -230,

              background:
                "rgba(255,255,255,0.08)",
            }}
          />

          {/* Decorative circles */}

          <Box
            sx={{
              position: "absolute",

              width: 80,
              height: 80,

              borderRadius: "50%",

              border:
                "2px solid rgba(255,255,255,0.3)",

              left: 35,
              top: 80,
            }}
          />

          <Box
            sx={{
              position: "absolute",

              width: 18,
              height: 18,

              borderRadius: "50%",

              backgroundColor:
                "rgba(255,255,255,0.5)",

              right: 80,
              top: 90,
            }}
          />

          {/* CONTENT */}

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* Heading */}

            <Typography
              sx={{
                fontSize: {
                  md: "2rem",
                  lg: "2.65rem",
                },

                lineHeight: 1.12,

                fontWeight: 800,

                maxWidth: 600,
              }}
            >
              Efficient Workshop
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  md: "1.8rem",
                  lg: "2.3rem",
                },

                fontWeight: 800,

                lineHeight: 1.15,

                color: "#38bdf8",
              }}
            >
              for Better Mobility
            </Typography>

            <Box
              sx={{
                width: 90,
                height: 3,
                backgroundColor: "#38bdf8",
                my: 2,
                borderRadius: 2,
              }}
            />

            <Typography
              sx={{
                maxWidth: 560,

                color:
                  "rgba(255,255,255,0.88)",

                fontSize: {
                  md: "0.9rem",
                  lg: "1rem",
                },

                lineHeight: 1.7,
              }}
            >
              Manage coach receipt, yard
              movement, shop work,
              inspection and outturn from
              one integrated system.
            </Typography>

            {/* COACH ILLUSTRATION */}

            <Box
              sx={{
                height: {
                  md: 250,
                  lg: 290,
                },

                position: "relative",

                mt: 3,

                display: "flex",

                alignItems: "center",

                justifyContent: "center",
              }}
            >
              {/* Workshop floor */}

              <Box
                sx={{
                  position: "absolute",

                  bottom: 15,

                  left: 0,
                  right: 0,

                  height: 35,

                  borderTop:
                    "6px solid rgba(255,255,255,0.65)",

                  borderBottom:
                    "6px solid rgba(255,255,255,0.3)",

                  transform:
                    "perspective(300px) rotateX(55deg)",

                  opacity: 0.7,
                }}
              />

              {/* Coach body */}

              <Box
                sx={{
                  position: "relative",

                  width: {
                    md: 380,
                    lg: 450,
                  },

                  height: {
                    md: 130,
                    lg: 155,
                  },

                  borderRadius:
                    "28px 28px 12px 12px",

                  background:
                    "linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)",

                  border:
                    "5px solid #123b82",

                  boxShadow:
                    "0 25px 45px rgba(0,0,0,0.28)",

                  transform:
                    "perspective(700px) rotateY(-5deg)",
                }}
              >
                {/* Coach blue stripe */}

                <Box
                  sx={{
                    position: "absolute",

                    left: 0,
                    right: 0,

                    top: "50%",

                    height: 23,

                    transform:
                      "translateY(-50%)",

                    background:
                      "linear-gradient(90deg, #0b65d8, #1d4ed8)",
                  }}
                />

                {/* Coach windows */}

                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    position: "absolute",

                    top: 22,

                    left: 45,
                    right: 45,

                    justifyContent:
                      "space-between",
                  }}
                >
                  {[1, 2, 3, 4, 5].map(
                    (item) => (
                      <Box
                        key={item}
                        sx={{
                          width: {
                            md: 48,
                            lg: 57,
                          },

                          height: {
                            md: 42,
                            lg: 48,
                          },

                          borderRadius: 1.5,

                          background:
                            "linear-gradient(135deg, #0f3c83, #60a5fa)",

                          border:
                            "3px solid #1e3a8a",

                          boxShadow:
                            "inset 0 3px 8px rgba(255,255,255,0.25)",
                        }}
                      />
                    )
                  )}
                </Stack>

                {/* Coach doors */}

                <Box
                  sx={{
                    position: "absolute",

                    right: 10,

                    top: 40,

                    width: 20,
                    height: 90,

                    border:
                      "3px solid #1e3a8a",

                    borderRadius: 1,

                    background:
                      "linear-gradient(135deg, #bfdbfe, #60a5fa)",
                  }}
                />
 <Box
                  sx={{
                    position: "absolute",

                    left: 10,

                    top: 40,

                    width: 20,
                    height: 90,

                    border:
                      "3px solid #1e3a8a",

                    borderRadius: 1,

                    background:
                      "linear-gradient(135deg, #bfdbfe, #60a5fa)",
                  }}
                />
                

                {/* Wheels */}

                <Box
                  sx={{
                    position: "absolute",

                    bottom: -28,

                    left: 55,

                    width: 52,
                    height: 52,

                    borderRadius: "50%",

                    background: "#111827",

                    border:
                      "9px solid #64748b",
                  }}
                />



                <Box
                  sx={{
                    position: "absolute",

                    bottom: -28,

                    right: 240,

                    width: 52,
                    height: 52,

                    borderRadius: "50%",

                    background: "#111827",

                    border:
                      "9px solid #64748b",
                  }}
                />

 <Box
                  sx={{
                    position: "absolute",

                    bottom: -28,

                    right: 150,

                    width: 52,
                    height: 52,

                    borderRadius: "50%",

                    background: "#111827",

                    border:
                      "9px solid #64748b",
                  }}
                />



                <Box
                  sx={{
                    position: "absolute",

                    bottom: -28,

                    right: 55,

                    width: 52,
                    height: 52,

                    borderRadius: "50%",

                    background: "#111827",

                    border:
                      "9px solid #64748b",
                  }}
                />
              </Box>

             
            </Box>

            {/* FEATURE CARDS */}

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  "repeat(4, 1fr)",

                gap: 1.2,

                mt: 1,
              }}
            >
              {features.map(
                (feature) => (
                  <Box
                    key={feature.title}
                    sx={{
                      p: 1.4,

                      minHeight: 112,

                      borderRadius: 2.5,

                      background:
                        "rgba(255,255,255,0.10)",

                      border:
                        "1px solid rgba(255,255,255,0.18)",

                      backdropFilter:
                        "blur(10px)",

                      transition:
                        "all 0.25s ease",

                      "&:hover": {
                        background:
                          "rgba(255,255,255,0.17)",

                        transform:
                          "translateY(-3px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,

                        borderRadius: "50%",

                        display: "flex",

                        alignItems: "center",

                        justifyContent:
                          "center",

                        mb: 1,

                        backgroundColor:
                          "rgba(255,255,255,0.16)",

                        color: "#fff",
                      }}
                    >
                      {React.cloneElement(
                        feature.icon,
                        {
                          sx: {
                            fontSize: 20,
                          },
                        }
                      )}
                    </Box>

                    <Typography
                      fontWeight={700}
                      fontSize="0.8rem"
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,

                        fontSize:
                          "0.68rem",

                        lineHeight: 1.4,

                        color:
                          "rgba(255,255,255,0.72)",
                      }}
                    >
                      {
                        feature.description
                      }
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;